import { useSyncExternalStore } from "react";

/** Polymarket's two public hosts. PolyGains' own API is tracked by useApiStatus. */
export type PublicSource = "gamma" | "data-api";
export type ViewSource = PublicSource | "polygains" | "wallet file";

type Health = "ok" | "down";

let health: Partial<Record<PublicSource, Health>> = {};
const listeners = new Set<() => void>();

/** `pm:` SWR keys are Polymarket calls; trades and holders come from data-api, the rest from gamma. */
function sourceOf(key: string): PublicSource | null {
	if (!key.includes("pm:")) return null;
	return /pm:(trades|holders)/.test(key) ? "data-api" : "gamma";
}

/** Fed by every SWR result, so the footer reports what really happened rather than a probe. */
export function recordSource(key: string, ok: boolean) {
	const source = sourceOf(key);
	const next: Health = ok ? "ok" : "down";
	if (!source || health[source] === next) return;
	health = { ...health, [source]: next };
	for (const notify of listeners) notify();
}

const subscribe = (fn: () => void) => {
	listeners.add(fn);
	return () => listeners.delete(fn);
};

export const useSourceHealth = () => useSyncExternalStore(subscribe, () => health);

const VIEWS: [string, ViewSource[]][] = [
	["/smart-money", ["wallet file"]],
	["/segments", ["wallet file"]],
	["/wallets", ["polygains"]],
	["/flagged", ["polygains"]],
	["/terminal", ["polygains"]],
	["/dashboard", ["polygains"]],
	["/users", ["polygains"]],
	["/whales", ["data-api"]],
	["/watchlist", ["gamma"]],
];

/** The sources a view draws on. Anything unlisted is the markets view. */
export function viewSources(path: string): ViewSource[] {
	const hit = VIEWS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
	return hit ? hit[1] : ["gamma", "data-api", "polygains"];
}
